/**
 * Public-facing API reference.
 *
 * Documents the v1 REST surface: authentication via account-tied API keys,
 * /v1/scans for programmatic domain scans, and the read endpoints for
 * domains, alerts, and DMARC reports. Mirrors the legacy site's API page
 * so engineering customers don't lose the integration path post-rebuild.
 *
 * Single-page anchored — every endpoint is one section so engineers can
 * deep-link from internal docs. Keep the surface honest: only document
 * what we actually ship.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Code as CodeIcon,
  KeyRound,
  BookOpen,
  Lock,
  Webhook,
  AlertTriangle,
  ArrowRight,
  Bot,
} from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';

interface Endpoint {
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH';
  path: string;
  summary: string;
  body?: string;
  example?: string;
}

const ENDPOINTS: Endpoint[] = [
  {
    method: 'POST',
    path: '/scan-domain',
    summary: 'Run a deliverability scan against a domain. Returns the full check stack: SPF, DKIM, DMARC, MTA-STS, TLS-RPT, BIMI, MX TLS, and blocklist coverage.',
    body: '{ "domain": "example.com", "dkimSelectors": ["selector1"] }',
    example: `curl -X POST https://api.inboxguard.io/scan-domain \\
  -H "Authorization: Bearer ig_live_xxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"domain":"example.com"}'`,
  },
  {
    method: 'GET',
    path: '/scans',
    summary: 'List recent scans for your org. Supports ?domain=, ?limit=, ?cursor= for pagination.',
    example: `curl https://api.inboxguard.io/scans?limit=20 \\
  -H "Authorization: Bearer ig_live_xxxxxxxxxxxxxxxxxxxxxxxxxx"`,
  },
  {
    method: 'GET',
    path: '/domains',
    summary: 'List domains in your organization with their latest score and status.',
    example: `curl https://api.inboxguard.io/domains \\
  -H "Authorization: Bearer ig_live_xxxxxxxxxxxxxxxxxxxxxxxxxx"`,
  },
  {
    method: 'GET',
    path: '/domains/{id}',
    summary: 'Fetch one domain: full record list, last scan, and alert history.',
    example: `curl https://api.inboxguard.io/domains/d_01H... \\
  -H "Authorization: Bearer ig_live_xxxxxxxxxxxxxxxxxxxxxxxxxx"`,
  },
  {
    method: 'DELETE',
    path: '/domains/{id}',
    summary: 'Stop monitoring a domain. Historical scans are retained; future cron scans are not scheduled.',
  },
  {
    method: 'GET',
    path: '/domains/{id}/dmarc-reports',
    summary: 'List ingested DMARC RUA aggregate reports for a domain. Paginated via ?cursor=.',
  },
  {
    method: 'GET',
    path: '/alerts',
    summary: 'List alerts across all domains in your org. ?status=open|resolved, ?domain=, ?severity= filters supported.',
  },
  {
    method: 'PATCH',
    path: '/alerts/{id}',
    summary: 'Toggle alert resolution. Body { "resolved": true } sets resolved_at; { "resolved": false } clears it.',
    body: '{ "resolved": true }',
  },
  {
    method: 'POST',
    path: '/alerts/bulk-resolve',
    summary: 'Resolve many alerts in one call. Body { "ids": ["al_…", …] }, max 200 per call.',
    body: '{ "ids": ["al_01H...", "al_01H..."] }',
  },
  {
    method: 'POST',
    path: '/analyze-headers',
    summary: 'Pre-send header analyzer. Paste raw email headers, get a re-verified SPF/DKIM/DMARC/ARC chain. We don\'t trust Authentication-Results; we recompute.',
    body: '{ "headers": "Received: ...\\nDKIM-Signature: ...", "body": "...optional raw body..." }',
  },
  {
    method: 'GET',
    path: '/me',
    summary: 'Get the calling user + org context. Handy for verifying which workspace your key is scoped to.',
  },
];

export function ApiDocsPage() {
  return (
    <>
      <Helmet>
        <title>API reference — InboxGuard</title>
        <meta
          name="description"
          content="REST API for programmatic deliverability scans. Authenticate with an account-tied API key."
        />
        <link rel="canonical" href="https://inboxguard.io/api-docs" />
        <meta property="og:title" content="API reference — InboxGuard" />
        <meta property="og:url" content="https://inboxguard.io/api-docs" />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <section className="hero-grad">
          <div className="container mx-auto max-w-5xl px-4 pb-12 pt-16 sm:pt-20">
            <div className="mx-auto max-w-2xl text-center animate-fade-in-up">
              <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                <CodeIcon className="h-3 w-3" />
                v1 · stable
              </span>
              <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">API reference</h1>
              <p className="mx-auto mt-4 max-w-xl text-balance text-lg text-muted-foreground">
                A REST API for programmatic domain scans, alert management, and DMARC report
                ingestion. Account-tied API keys, JSON in / JSON out, idempotent reads.
              </p>
            </div>
          </div>
        </section>

        <main className="container mx-auto max-w-5xl px-4 pb-20 sm:pb-24">
          {/*
            min-w-0 on the grid container AND the content column. CSS Grid
            defaults `min-width: auto` on track items, which means a wide
            child (a long unwrappable code line, an `<inline code>` token)
            will force the track wider than the viewport — that's what was
            blowing the page out to ~713px on a 390px screen. `min-w-0`
            lets the inner overflow-x-auto on `<pre>` actually scroll
            within the column instead of stretching the layout.
          */}
          <div className="grid min-w-0 gap-8 lg:grid-cols-[220px_1fr]">
            <nav className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border bg-card p-4 text-sm">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  On this page
                </p>
                <ul className="space-y-1.5">
                  <li><a className="font-medium text-primary hover:underline" href="#auth">Authentication</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#base-url">Base URL</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#rate-limits">Rate limits</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#endpoints">Endpoints</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#errors">Errors</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#webhooks">Webhooks</a></li>
                  <li><a className="text-muted-foreground hover:text-foreground" href="#sdks">SDKs &amp; libraries</a></li>
                </ul>
              </div>
            </nav>

            <div className="min-w-0 space-y-12 [&_code]:break-words">
              {/* Agent builders get the full MCP / install-guide / fix-loop
                  treatment on /developers. This page is the focused REST
                  reference; just point them there and move on. */}
              <Link
                to="/developers"
                className="group flex items-start gap-4 rounded-lg border border-primary/30 bg-primary/5 p-5 transition-colors hover:border-primary/50 hover:bg-primary/10"
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Bot className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="flex items-center gap-1.5 text-base font-semibold tracking-tight">
                    Building an AI agent?
                    <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-0.5" />
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    The MCP server (<code className="break-all">https://mcp.inboxguard.io/mcp</code>),
                    its 28 tools, copy-paste configs for Claude Desktop / ChatGPT / Cursor / Claude
                    Code, and the scan → plan → apply fix loop live on the{' '}
                    <span className="font-medium text-primary group-hover:underline">Developers page</span>.
                    The REST reference below stays focused on plain HTTP.
                  </p>
                </div>
              </Link>

              <Section id="auth" icon={<KeyRound className="h-5 w-5" />} title="Authentication">
                <p>
                  Every request must include a bearer token issued from your account. API keys are
                  scoped to a single organization and inherit that org&apos;s plan limits.
                </p>
                <CodeBlock>
                  {`Authorization: Bearer ig_live_xxxxxxxxxxxxxxxxxxxxxxxxxx`}
                </CodeBlock>
                <p>
                  Manage keys in{' '}
                  <Link to="/settings#api-keys" className="font-medium text-primary hover:underline">
                    Settings → API keys
                  </Link>
                  . You only see the full secret once, at creation time. Revoking a key takes
                  effect immediately and is not reversible.
                </p>
                <Callout
                  tone="warn"
                  icon={<AlertTriangle className="h-4 w-4" />}
                  title="Treat keys like passwords"
                >
                  Never check API keys into source control or paste them into shared documents.
                  Server-to-server only. Keys exposed to a browser will be auto-revoked.
                </Callout>
              </Section>

              <Section id="base-url" icon={<Lock className="h-5 w-5" />} title="Base URL">
                <CodeBlock>{`https://api.inboxguard.io`}</CodeBlock>
                <p>
                  All endpoints require HTTPS. Plain HTTP requests are rejected at the edge. Replies
                  are JSON encoded UTF-8 with a stable schema across the v1 surface.
                </p>
              </Section>

              <Section id="rate-limits" icon={<BookOpen className="h-5 w-5" />} title="Rate limits">
                <p>
                  <code>/scan-domain</code> is metered per-org with a fixed hourly window. Read
                  endpoints (<code>GET /domains</code>, <code>GET /alerts</code>, etc.) are not
                  metered today.
                </p>
                <ul className="ml-6 list-disc space-y-1 text-sm">
                  <li><strong>Free:</strong> 20 scans/hour</li>
                  <li><strong>Entry:</strong> 200 scans/hour</li>
                  <li><strong>Pro:</strong> 1,000 scans/hour</li>
                  <li><strong>Agency:</strong> 5,000 scans/hour</li>
                </ul>
                <p>
                  When throttled the API returns <code>429</code> with a{' '}
                  <code>Retry-After</code> header (seconds). A separate per-IP safety net (2,000/hr
                  across all callers from one IP) prevents abuse from rotating accounts and rarely
                  trips legitimate office traffic.
                </p>
              </Section>

              <Section id="endpoints" icon={<CodeIcon className="h-5 w-5" />} title="Endpoints">
                <div className="space-y-6">
                  {ENDPOINTS.map((e) => (
                    <EndpointBlock key={`${e.method}-${e.path}`} endpoint={e} />
                  ))}
                </div>
              </Section>

              <Section id="errors" icon={<AlertTriangle className="h-5 w-5" />} title="Errors">
                <p>Every error body has the shape:</p>
                <CodeBlock>
                  {`{
  "error": "Domain not found",
  "code": "NOT_FOUND",
  "requestId": "req_01H..."
}`}
                </CodeBlock>
                <p>Status codes:</p>
                <ul className="ml-6 list-disc space-y-1 text-sm">
                  <li><code>400</code> — invalid request body or domain format</li>
                  <li><code>401</code> — missing / malformed / revoked API key</li>
                  <li><code>403</code> — key valid but lacks permission for the resource</li>
                  <li><code>404</code> — resource does not exist (or belongs to another org)</li>
                  <li><code>429</code> — rate limited (see <code>Retry-After</code>)</li>
                  <li><code>5xx</code> — surface to support; <code>requestId</code> is the lookup handle</li>
                </ul>
              </Section>

              <Section id="webhooks" icon={<Webhook className="h-5 w-5" />} title="Webhooks">
                <p>
                  Configure a webhook channel under{' '}
                  <Link to="/settings#notifications" className="font-medium text-primary hover:underline">
                    Notifications
                  </Link>
                  {' '}to receive HTTPS POSTs when an alert fires or resolves. Payloads include
                  the alert plus enough context to route in your own system (org, domain, severity,
                  kind, message).
                </p>
                <CodeBlock>
                  {`POST /your-handler
Content-Type: application/json
User-Agent: InboxGuard-Webhook/1
X-InboxGuard-Signature: sha256=<hex hmac of the raw body>
X-InboxGuard-Timestamp: 1718064000

{
  "event": "alert.opened",
  "alert": {
    "id": "al_01H...",
    "domain": "example.com",
    "severity": "critical",
    "kind": "dmarc_regression",
    "message": "DMARC posture downgraded: reject → none"
  }
}`}
                </CodeBlock>
                <Callout
                  tone="info"
                  icon={<Lock className="h-4 w-4" />}
                  title="Verify the signature"
                >
                  Every delivery is signed: <code>X-InboxGuard-Signature</code> is an HMAC-SHA256 of
                  the raw request body, keyed with your channel&apos;s signing secret (shown once at
                  channel creation). Recompute it over the exact bytes you receive and compare in
                  constant time; reject deliveries whose <code>X-InboxGuard-Timestamp</code> is
                  older than a few minutes to stop replays.
                </Callout>
              </Section>

              <Section id="sdks" icon={<BookOpen className="h-5 w-5" />} title="SDKs &amp; libraries">
                <p>
                  The REST API is the primary surface, and the{' '}
                  <a
                    href="/openapi.json"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    OpenAPI 3.1 contract
                  </a>{' '}
                  is typed end-to-end. Point any generator at it for a client in your language, or
                  just call the endpoints with a standard HTTP library.
                </p>
                <p>
                  Official TypeScript and Python clients (typed helpers, retry-on-429, pagination)
                  are built and publish-ready; the <code>inboxguard</code> packages on npm and PyPI
                  are pending publication. Track status and the full agent surface on the{' '}
                  <Link to="/developers#api" className="font-medium text-primary hover:underline">
                    Developers page
                  </Link>
                  .
                </p>
              </Section>

              <div className="rounded-lg border bg-card p-6 text-center">
                <h3 className="text-lg font-semibold tracking-tight">Need higher volume or a custom integration?</h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                  Agency and enterprise plans include custom rate limits, dedicated DMARC ingest
                  endpoints, and onboarding support.
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:underline"
                >
                  Talk to sales
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}

function Section({
  id,
  icon,
  title,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-3 border-b pb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent text-accent-foreground">
          {icon}
        </div>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground">{children}</div>
    </section>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <pre className="overflow-x-auto rounded-md border bg-slate-900 px-4 py-3 font-mono text-xs leading-relaxed text-slate-100 shadow-sm dark:bg-slate-800">
      <code>{children}</code>
    </pre>
  );
}

function EndpointBlock({ endpoint }: { endpoint: Endpoint }) {
  // Methods are categorical, not brand. GET (safe read) carries the emerald
  // accent; the mutating verbs are graded slate → amber → red so they stay
  // distinct from GET and from each other without reintroducing a second hue.
  const methodColor: Record<Endpoint['method'], string> = {
    GET: 'bg-primary/10 text-primary',
    POST: 'bg-secondary text-foreground',
    DELETE: 'bg-destructive/10 text-destructive',
    PATCH: 'bg-warning/15 text-warning',
  };
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-2 py-0.5 font-mono text-xs font-semibold ${methodColor[endpoint.method]}`}
        >
          {endpoint.method}
        </span>
        <code className="font-mono text-sm text-foreground">{endpoint.path}</code>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{endpoint.summary}</p>
      {endpoint.body && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Request body
          </p>
          <CodeBlock>{endpoint.body}</CodeBlock>
        </div>
      )}
      {endpoint.example && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Example
          </p>
          <CodeBlock>{endpoint.example}</CodeBlock>
        </div>
      )}
    </div>
  );
}

function Callout({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'warn' | 'info';
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-primary/30 bg-primary/5';
  return (
    <div className={`flex gap-3 rounded-md border p-3 text-sm ${cls}`}>
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1">{children}</p>
      </div>
    </div>
  );
}

export default ApiDocsPage;
